import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  Descriptions,
  Tag,
  Spin,
  Row,
  Col,
  Avatar,
  Upload,
  message,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CameraOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { setUserData } from "features/user/userSlice";
import {
  getProfile,
  updateProfile,
  uploadProfilePhoto,
} from "service/profile";

const DEFAULT_AVATAR = "/static/images/users/default-avatar.jpg";

const formatDate = (s) => (s ? new Date(s).toLocaleString() : "—");

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const avatarSrc = (profile && profile.photo) || DEFAULT_AVATAR;

  // Bust the browser image cache when the same URL gets a new file uploaded
  const cacheBustedAvatar = profile?.photo
    ? `${profile.photo}${profile.photo.includes("?") ? "&" : "?"}t=${profile.updated_at || ""}`
    : DEFAULT_AVATAR;

  const onPhotoSelect = async (file) => {
    if (!file.type.startsWith("image/")) {
      message.error("Please select an image file.");
      return Upload.LIST_IGNORE;
    }
    if (file.size > 2 * 1024 * 1024) {
      message.error("Image must be smaller than 2 MiB.");
      return Upload.LIST_IGNORE;
    }
    try {
      setUploading(true);
      const updated = await uploadProfilePhoto(file);
      setProfile(updated);
      // Push the new photo URL into Redux so the navbar avatar updates immediately.
      dispatch(setUserData({ ...updated }));
    } finally {
      setUploading(false);
    }
    return Upload.LIST_IGNORE; // we manage state ourselves; don't track in Upload list
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await getProfile();
      setProfile(data);
      form.setFieldsValue({
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        email: data.email || "",
        phone: data.phone == null ? "" : String(data.phone),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async (values) => {
    setSaving(true);
    try {
      const updated = await updateProfile(values);
      setProfile(updated);
      dispatch(setUserData({ ...updated }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div style={{ padding: 24, maxWidth: 980, margin: "0 auto" }}>
      <h2 style={{ marginBottom: 16 }}>My Profile</h2>

      <Row gutter={24}>
        <Col xs={24} md={10}>
          <Card
            title="Account"
            size="small"
            style={{ marginBottom: 16 }}
            styles={{ body: { textAlign: "center" } }}
          >
            <div style={{ marginBottom: 14 }}>
              <Avatar
                size={120}
                src={cacheBustedAvatar}
                onError={() => true /* hide broken image; AntD shows initials */}
              />
              <div style={{ marginTop: 10 }}>
                <Upload
                  name="photo"
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={onPhotoSelect}
                >
                  <Button
                    icon={<CameraOutlined />}
                    loading={uploading}
                    size="small"
                  >
                    Change photo
                  </Button>
                </Upload>
              </div>
            </div>
            <Descriptions column={1} size="small" labelStyle={{ width: 130 }}>
              <Descriptions.Item label="Username">
                {profile.username}
              </Descriptions.Item>
              <Descriptions.Item label="Role">
                <Tag color={profile.role === "ROLE_ADMIN" ? "blue" : "default"}>
                  {profile.role || "—"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {profile.is_active ? (
                  <Tag color="green">Active</Tag>
                ) : (
                  <Tag color="red">Inactive</Tag>
                )}
                {profile.email_verified && (
                  <Tag color="cyan" style={{ marginLeft: 4 }}>
                    Email verified
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Joined">
                {formatDate(profile.date_joined)}
              </Descriptions.Item>
              <Descriptions.Item label="Last login">
                {formatDate(profile.last_login)}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} md={14}>
          <Card title="Edit profile" size="small">
            <Form
              form={form}
              layout="vertical"
              onFinish={onSave}
              initialValues={{
                first_name: profile.first_name,
                last_name: profile.last_name,
                email: profile.email,
                phone: profile.phone == null ? "" : String(profile.phone),
              }}
            >
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="first_name" label="First name">
                    <Input prefix={<UserOutlined />} maxLength={150} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="last_name" label="Last name">
                    <Input prefix={<UserOutlined />} maxLength={150} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input prefix={<MailOutlined />} />
              </Form.Item>
              <Form.Item
                name="phone"
                label="Phone"
                rules={[
                  {
                    pattern: /^\d*$/,
                    message: "Digits only",
                  },
                ]}
              >
                <Input prefix={<PhoneOutlined />} placeholder="Digits only" />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" loading={saving}>
                  Save changes
                </Button>
                <Button style={{ marginLeft: 8 }} onClick={() => form.resetFields()}>
                  Reset
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Profile;
